import React, { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import auth from "services/auth";
import api from "services/api";
import AppContext from "contexts/AppContext";
import { __ } from "services/Translator";
import { consumeStoredFlow } from "utils/socialOauth";
import { getProviderMeta } from "configs/socialProviders";

/**
 * Handles the redirect back from an authorization-code social provider
 * (Discord, Twitch, Kick, X). Reads the one-time code + state from the URL,
 * validates the state against the value we stashed before redirecting, and posts
 * the code (plus the PKCE verifier where required) to the backend, which does the
 * server-side token exchange and profile fetch. On success the returned session is
 * stored exactly like every other login.
 */
const SocialCallback = () => {
  const history = useHistory();
  const { provider } = useParams();
  const { updateUserAuthStatus } = React.useContext(AppContext);
  const [message, setMessage] = useState(__("Signing you in..."));
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const run = async () => {
      const meta = getProviderMeta(provider);
      const url = new URL(window.location.href);
      const code = url.searchParams.get("code");
      const returnedState = url.searchParams.get("state");
      const providerError = url.searchParams.get("error");

      const flow = consumeStoredFlow();

      if (providerError) {
        setMessage(__("Login was cancelled."));
        return finish("/");
      }
      if (!meta || !code || !flow || flow.provider !== meta.key) {
        setMessage(__("This login link is invalid or has expired."));
        return finish("/");
      }
      // State must match what we stashed (CSRF protection on the redirect).
      if (!returnedState || returnedState !== flow.state) {
        setMessage(__("This login link is invalid or has expired."));
        return finish("/");
      }

      const payload = {
        provider: meta.key,
        code: code,
        redirect_uri: flow.redirect_uri,
        // Consent captured before the redirect; the backend only creates a
        // first-time account when this is present.
        agreeToTerms: flow.agreeToTerms ? 1 : 0,
        acceptCountryLimitation: flow.acceptCountryLimitation ? 1 : 0,
      };
      if (flow.code_verifier) {
        payload.code_verifier = flow.code_verifier;
      }

      try {
        const response = await api.post("/social-login", payload);
        if (response.data && response.data.status === "success" && response.data.user) {
          await auth.login(response.data.user);
          updateUserAuthStatus(response.data.user);
          return finish("/games/all");
        }
        setMessage((response.data && response.data.message) || __("We could not sign you in."));
        return finish("/");
      } catch (e) {
        setMessage(__("We could not sign you in."));
        return finish("/");
      }
    };

    const finish = (path) => {
      setTimeout(() => history.push(path), 1200);
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="social-callback-screen">
      <div className="social-callback-screen__inner">
        <div className="social-callback-screen__spinner" />
        <p className="social-callback-screen__msg">{message}</p>
      </div>
    </div>
  );
};

export default SocialCallback;
